<template>
  <div
    class="bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 h-screen flex flex-col"
    :class="isCollapsed ? 'w-20' : 'w-64'"
  >
    <!-- Logo -->
    <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 h-16 flex-shrink-0">
      <div v-if="!isCollapsed" class="flex items-center gap-3">
        <div class="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
          <i class="pi pi-briefcase text-white"></i>
        </div>
        <span class="font-bold text-gray-900 dark:text-white">{{ t('app.hrShort') }}</span>
      </div>
      <button
        @click="toggleSidebar"
        class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
      >
        <i :class="isCollapsed ? 'pi pi-bars' : 'pi pi-chevron-left'" class="text-gray-600 dark:text-gray-400"></i>
      </button>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto p-4 space-y-2">
      <div v-if="!isCollapsed" class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-3 mb-4">
        {{ t('nav.mainMenu') }}
      </div>

      <RouterLink
        v-for="item in menuItems"
        :key="item.to"
        :to="item.to"
        :title="isCollapsed ? item.label : ''"
        :class="[
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
          $route.path.startsWith(item.to)
            ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 font-semibold'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
        ]"
      >
        <i :class="item.icon" class="text-lg flex-shrink-0"></i>
        <span v-if="!isCollapsed" class="text-sm">{{ item.label }}</span>
      </RouterLink>

      <div v-if="!isCollapsed" class="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
        <div class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-3 mb-4">
          {{ t('nav.management') }}
        </div>

        <RouterLink
          v-for="item in adminMenuItems"
          :key="item.to"
          :to="item.to"
          :class="[
            'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
            $route.path.startsWith(item.to)
              ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 font-semibold'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          ]"
        >
          <i :class="item.icon" class="text-lg flex-shrink-0"></i>
          <span class="text-sm">{{ item.label }}</span>
        </RouterLink>
      </div>
    </nav>

    <!-- User Profile -->
    <div
      class="border-t border-gray-200 dark:border-gray-700 p-4 flex-shrink-0"
      :class="isCollapsed ? 'flex justify-center' : 'space-y-3'"
    >
      <div v-if="!isCollapsed" class="flex items-center gap-3">
        <Avatar :label="userInitials" :style="{ backgroundColor: '#488BE6' }" shape="circle" size="large" />
        <div class="min-w-0">
          <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ userFullName }}</p>
          <p class="text-xs text-gray-600 dark:text-gray-400 truncate">{{ userRole }}</p>
        </div>
      </div>

      <button
        v-if="!isCollapsed"
        @click="logout"
        class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-sm"
      >
        <i class="pi pi-sign-out"></i>
        <span>{{ t('common.logoutShort') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Avatar from 'primevue/avatar'
import { useUIStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import { useAuth } from '@/composables/useAuth'
import { useFormatters } from '@/composables/useFormatters'
import { usePermission } from '@/composables/usePermission'

const { t, te } = useI18n()
const uiStore = useUIStore()
const authStore = useAuthStore()
const { logout } = useAuth()
const { getInitials } = useFormatters()
const { hasPermission } = usePermission()

const isCollapsed = computed(() => uiStore.sidebarCollapsed)

const userFullName = computed(() => authStore.fullName)
const userRole = computed(() => {
  const role = authStore.user?.role
  if (!role) return t('common.user')
  return te(`roles.${role}`) ? t(`roles.${role}`) : t('common.user')
})

const userInitials = computed(() => {
  if (authStore.user) {
    return getInitials(authStore.user.firstName, authStore.user.lastName)
  }
  return 'U'
})

const menuItems = computed(() => [
  {
    label: t('nav.dashboard'),
    to: '/dashboard',
    icon: 'pi pi-home'
  },
  {
    label: t('nav.employees'),
    to: '/dashboard/employees',
    icon: 'pi pi-users',
    visible: hasPermission('view_employees')
  },
  {
    label: t('nav.projects'),
    to: '/dashboard/projects',
    icon: 'pi pi-list',
    visible: hasPermission('view_projects')
  },
  {
    label: t('nav.requests'),
    to: '/dashboard/requests',
    icon: 'pi pi-inbox',
    visible: hasPermission('view_requests')
  },
  {
    label: t('nav.inventory'),
    to: '/dashboard/inventory',
    icon: 'pi pi-box',
    visible: hasPermission('view_inventory')
  },
  {
    label: t('nav.procurement'),
    to: '/dashboard/procurement-requests',
    icon: 'pi pi-shopping-cart',
    visible: hasPermission('view_procurement_requests')
  },
  {
    label: t('nav.payroll'),
    to: '/dashboard/payroll',
    icon: 'pi pi-money-bill',
    visible: hasPermission('view_payroll')
  },
  {
    label: t('nav.documents'),
    to: '/dashboard/documents',
    icon: 'pi pi-file',
    visible: hasPermission('view_documents')
  },
  {
    label: t('nav.reports'),
    to: '/dashboard/reports',
    icon: 'pi pi-chart-bar',
    visible: hasPermission('view_reports')
  },
  {
    label: t('nav.discipline'),
    to: '/dashboard/discipline',
    icon: 'pi pi-flag',
    visible: hasPermission('manage_discipline')
  },
  {
    label: t('nav.sendNotification'),
    to: '/dashboard/send-notification',
    icon: 'pi pi-send',
    visible: hasPermission('send_notification')
  },
  {
    label: t('nav.myAssets'),
    to: '/dashboard/my-assets',
    icon: 'pi pi-box'
  }
].filter((item) => item.visible !== false))

const adminMenuItems = computed(() => [
  {
    label: t('nav.settings'),
    to: '/dashboard/settings',
    icon: 'pi pi-cog',
    visible: hasPermission('view_settings')
  },
  {
    label: t('nav.organization'),
    to: '/dashboard/org-hierarchy',
    icon: 'pi pi-sitemap',
    visible: hasPermission('view_org_hierarchy')
  },
  {
    label: t('nav.logs'),
    to: '/dashboard/logs',
    icon: 'pi pi-history',
    visible: hasPermission('view_audit_logs')
  }
].filter((item) => item.visible !== false))

function toggleSidebar() {
  uiStore.toggleSidebar()
}
</script>
